import {
  EntireRangeUnit,
  type NullableDateFilterState,
} from '@/features/Commons/EntireDatePresetFilter';
import { FeedbackListTarget } from '@/features/Taxonomy/components/Topic2Detail/queries/useTopicDetailQueries';
import { getTimezoneDayjs } from '@/utils/time';

const TAXONOMY_TOPIC_DETAIL_FILTER_QUERY_KEY = 'dynamic_filter_set';
const TAXONOMY_TOPIC_DETAIL_TAB_QUERY_KEY = 'tab';
const TAXONOMY_TOPIC_DETAIL_SEARCH_QUERY_KEY = 'q';
const TAXONOMY_TOPIC_DETAIL_RANGE_UNIT_QUERY_KEY = 'range_unit';
const TAXONOMY_TOPIC_DETAIL_PRESET_QUERY_KEY = 'preset_item';
const TAXONOMY_TOPIC_DETAIL_DATE_START_QUERY_KEY = 'date_start';
const TAXONOMY_TOPIC_DETAIL_DATE_END_QUERY_KEY = 'date_end';

type TopicDetailUrlState = {
  filterSet?: StructuredFilter;
  searchTerm: string;
  selectedTab: FeedbackListTarget;
  dateState: NullableDateFilterState;
};

const parseJson = <T>(value: string | null): T | undefined => {
  if (!value) return undefined;

  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
};

const isEntireRangeUnit = (value: number): value is EntireRangeUnit => {
  return Object.values(EntireRangeUnit).includes(value);
};

const parseRangeUnit = (value: string | null): EntireRangeUnit => {
  if (!value) {
    return EntireRangeUnit.Entire;
  }

  const parsed = Number(value);
  if (!Number.isNaN(parsed) && isEntireRangeUnit(parsed)) {
    return parsed;
  }

  return EntireRangeUnit.Entire;
};

const parsePresetItem = (value: string | null): number | null => {
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseTopicDetailDateState = (
  searchParams: URLSearchParams,
): NullableDateFilterState => {
  const rangeUnit = parseRangeUnit(
    searchParams.get(TAXONOMY_TOPIC_DETAIL_RANGE_UNIT_QUERY_KEY),
  );

  if (rangeUnit === EntireRangeUnit.Entire) {
    return {
      dateRange: null,
      presetItem: null,
      rangeUnit,
    };
  }

  const startDate = searchParams.get(TAXONOMY_TOPIC_DETAIL_DATE_START_QUERY_KEY);
  const endDate = searchParams.get(TAXONOMY_TOPIC_DETAIL_DATE_END_QUERY_KEY);

  return {
    dateRange:
      startDate && endDate
        ? [getTimezoneDayjs(startDate), getTimezoneDayjs(endDate)]
        : null,
    presetItem: parsePresetItem(
      searchParams.get(TAXONOMY_TOPIC_DETAIL_PRESET_QUERY_KEY),
    ),
    rangeUnit,
  };
};

export const parseTopicDetailSearchParams = (
  searchParams: URLSearchParams,
): TopicDetailUrlState => {
  const selectedTab =
    searchParams.get(TAXONOMY_TOPIC_DETAIL_TAB_QUERY_KEY) === 'unlinked'
      ? 'unlinked'
      : 'total';

  return {
    filterSet: parseJson<StructuredFilter>(
      searchParams.get(TAXONOMY_TOPIC_DETAIL_FILTER_QUERY_KEY),
    ),
    searchTerm: searchParams.get(TAXONOMY_TOPIC_DETAIL_SEARCH_QUERY_KEY) ?? '',
    selectedTab:
      selectedTab === 'unlinked'
        ? FeedbackListTarget.UNLINKED
        : FeedbackListTarget.TOTAL,
    dateState: parseTopicDetailDateState(searchParams),
  };
};

export const buildTopicDetailSearchParams = ({
  currentSearchParams,
  filterSet,
  searchTerm,
  selectedTab,
  dateState,
}: {
  currentSearchParams: URLSearchParams;
  filterSet?: StructuredFilter;
  searchTerm: string;
  selectedTab: FeedbackListTarget;
  dateState: NullableDateFilterState;
}) => {
  const next = new URLSearchParams(currentSearchParams);

  if (filterSet && Object.keys(filterSet).length > 0) {
    next.set(TAXONOMY_TOPIC_DETAIL_FILTER_QUERY_KEY, JSON.stringify(filterSet));
  } else {
    next.delete(TAXONOMY_TOPIC_DETAIL_FILTER_QUERY_KEY);
  }

  const trimmedSearchTerm = searchTerm.trim();
  if (trimmedSearchTerm) {
    next.set(TAXONOMY_TOPIC_DETAIL_SEARCH_QUERY_KEY, trimmedSearchTerm);
  } else {
    next.delete(TAXONOMY_TOPIC_DETAIL_SEARCH_QUERY_KEY);
  }

  if (selectedTab === FeedbackListTarget.UNLINKED) {
    next.set(TAXONOMY_TOPIC_DETAIL_TAB_QUERY_KEY, 'unlinked');
  } else {
    next.delete(TAXONOMY_TOPIC_DETAIL_TAB_QUERY_KEY);
  }

  if (dateState.rangeUnit === EntireRangeUnit.Entire) {
    next.delete(TAXONOMY_TOPIC_DETAIL_RANGE_UNIT_QUERY_KEY);
    next.delete(TAXONOMY_TOPIC_DETAIL_PRESET_QUERY_KEY);
    next.delete(TAXONOMY_TOPIC_DETAIL_DATE_START_QUERY_KEY);
    next.delete(TAXONOMY_TOPIC_DETAIL_DATE_END_QUERY_KEY);
    return next;
  }

  next.set(
    TAXONOMY_TOPIC_DETAIL_RANGE_UNIT_QUERY_KEY,
    String(dateState.rangeUnit),
  );

  if (dateState.presetItem === null) {
    next.delete(TAXONOMY_TOPIC_DETAIL_PRESET_QUERY_KEY);
  } else {
    next.set(
      TAXONOMY_TOPIC_DETAIL_PRESET_QUERY_KEY,
      String(dateState.presetItem),
    );
  }

  if (dateState.dateRange) {
    next.set(
      TAXONOMY_TOPIC_DETAIL_DATE_START_QUERY_KEY,
      dateState.dateRange[0].toJSON(),
    );
    next.set(
      TAXONOMY_TOPIC_DETAIL_DATE_END_QUERY_KEY,
      dateState.dateRange[1].toJSON(),
    );
  } else {
    next.delete(TAXONOMY_TOPIC_DETAIL_DATE_START_QUERY_KEY);
    next.delete(TAXONOMY_TOPIC_DETAIL_DATE_END_QUERY_KEY);
  }

  return next;
};

export type { TopicDetailUrlState };
